import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Row } from 'primereact/row';
import { ColumnGroup } from 'primereact/columngroup';
import './summary.css';
import axios from 'axios';
import { Api } from '../../../services/api';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { BsFillInfoCircleFill } from 'react-icons/bs';
import { PiArrowsClockwiseBold } from 'react-icons/pi';

const GOAL_FIELD_BY_LABEL = {
  income: 'gross_sales',
  totalincome: 'income_2',
  grosssales: 'gross_sales',
  grossprofit: 'gross_profit',
  operatingexpenses: 'less_total_operating_expense',
  lesstotaloperatingexpense: 'less_total_operating_expense',
  ofsales: 'less_total_operating_expense',
  lidamultifamilyprojects: 'multi_family',
  lidamultifamilyprojectsofrevenue: 'multi_family_revenue',
  customhomesprojects: 'custom_home',
  customhomesprojectsofrevenue: 'custom_home_revenue',
  renovationsprojects: 'renovations',
  renovationsprojectsofrevenue: 'renovations_revenue',
  commercialprojects: 'commercial',
  commercialprojectsofrevenue: 'commercial_revenue',
  tenentimprovementsprojects: 'tenent_improvements',
  tenantimprovementsprojects: 'tenent_improvements',
  tenentimprovementprojectsofrevenue: 'tenent_improvements_revenue',
  tenantimprovementprojectsofrevenue: 'tenent_improvements_revenue',
  servicemaintenanceprojects: 'service_and_maintenance',
  servicemaintenanceprojectsofrevenue: 'service_and_maintenance_revenue',
  pocfromwip: 'poc_from_wip',
  otherfromwip: 'other_from_wip',
  pocfromprojection: 'poc_from_projection',
  otherfromprojection: 'other_from_projection',
  projectedoperatingcashflow: 'project_ocf'
};

const GOAL_DEFAULTS = {
  gross_sales: 0,
  income_2: 0,
  gross_profit: 0,
  gross_profit_2: 0,
  less_total_operating_expense: 0,
  less_total_operating_expense_2: 0,
  multi_family: 0,
  multi_family_revenue: 0,
  custom_home: 0,
  custom_home_revenue: 0,
  renovations: 0,
  renovations_revenue: 0,
  commercial: 0,
  commercial_revenue: 0,
  tenent_improvements: 0,
  tenent_improvements_revenue: 0,
  service_and_maintenance: 0,
  service_and_maintenance_revenue: 0,
  poc_from_wip: 0,
  other_from_wip: 0,
  poc_from_projection: 0,
  other_from_projection: 0,
  project_ocf: 0,
  projected_operating_cashflow: 0
};

const SUMMARY_VIEW = {
  CALCULATED: 'calculated',
  MANUAL: 'manual'
};

const normalizeText = (value) => (value || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value).replace(/[$,%\s,]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getYearFromColumn = (columnField) => {
  const match = String(columnField || '').match(/(20\d{2})/);
  return match ? match[1] : null;
};

const getGoalScopeFromColumn = (columnField) => {
  const year = getYearFromColumn(columnField);
  if (year) {
    return year;
  }

  if (isTop25Column(columnField)) {
    return 'top25';
  }

  return null;
};

const isTop25Column = (columnField) => {
  const normalized = normalizeText(columnField);
  return normalized === 'top25' || normalized === 'top25percent';
};

const formatCurrency = (value) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2
});


const Summary = () => {
  const toast = useRef(null);
  const [summaryRowsByView, setSummaryRowsByView] = useState({
    [SUMMARY_VIEW.CALCULATED]: [],
    [SUMMARY_VIEW.MANUAL]: []
  });
  const [activeView, setActiveView] = useState(SUMMARY_VIEW.CALCULATED);
  const [columnName, setColumnName] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const firstColumnName = columnName[0] || 'Sales';
  const projectionSummary = summaryRowsByView[activeView] || [];

  const getRowLabel = (rowData) => rowData?.[firstColumnName] || rowData?.Sales || '';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  };

  const getGoalFieldFromRow = (rowData) => {
    const rawLabel = getRowLabel(rowData);
    // Rows with % in their label are calculated/display-only — never editable
    if (rawLabel.includes('%')) return null;

    const normalizedLabel = normalizeText(rawLabel);
    const currentRowIndex = projectionSummary.findIndex((row) => row === rowData);

    if (normalizedLabel === 'income') {
      if (currentRowIndex > -1) {
        const incomeRowsBefore = projectionSummary
          .slice(0, currentRowIndex)
          .filter((row) => normalizeText(getRowLabel(row)) === 'income').length;

        return incomeRowsBefore > 0 ? 'income_2' : 'gross_sales';
      }
      return 'gross_sales';
    }

    if (normalizedLabel === 'grossprofit') {
      if (currentRowIndex > -1) {
        const grossProfitRowsBefore = projectionSummary
          .slice(0, currentRowIndex)
          .filter((row) => normalizeText(getRowLabel(row)) === 'grossprofit').length;

        return grossProfitRowsBefore > 0 ? 'gross_profit_2' : 'gross_profit';
      }
      return 'gross_profit';
    }

    if (
      normalizedLabel === 'operatingexpenses' ||
      normalizedLabel === 'lesstotaloperatingexpense'
    ) {
      if (currentRowIndex > -1) {
        const operatingRowsBefore = projectionSummary
          .slice(0, currentRowIndex)
          .filter((row) => {
            const label = normalizeText(getRowLabel(row));
            return label === 'operatingexpenses' || label === 'lesstotaloperatingexpense';
          }).length;

        return operatingRowsBefore > 0 ? 'less_total_operating_expense_2' : 'less_total_operating_expense';
      }
      return 'less_total_operating_expense';
    }

    if (GOAL_FIELD_BY_LABEL[normalizedLabel]) {
      return GOAL_FIELD_BY_LABEL[normalizedLabel];
    }

    if (normalizedLabel.includes('totalincome')) return 'income_2';
    if (normalizedLabel.includes('grosssales')) return 'gross_sales';
    if (normalizedLabel.includes('percent') || normalizedLabel.includes('ofsales')) return null;
    if (normalizedLabel.includes('grossprofit')) return 'gross_profit';
    if (normalizedLabel.includes('operatingexpense')) return 'less_total_operating_expense';
    if (normalizedLabel.includes('lesstotaloperatingexpense')) return 'less_total_operating_expense';
    if (normalizedLabel.includes('multifamily') && normalizedLabel.includes('revenue')) return 'multi_family_revenue';
    if (normalizedLabel.includes('multifamily')) return 'multi_family';
    if (normalizedLabel.includes('customhome') && normalizedLabel.includes('revenue')) return 'custom_home_revenue';
    if (normalizedLabel.includes('customhome')) return 'custom_home';
    if (normalizedLabel.includes('renovation') && normalizedLabel.includes('revenue')) return 'renovations_revenue';
    if (normalizedLabel.includes('renovation')) return 'renovations';
    if (normalizedLabel.includes('commercial') && normalizedLabel.includes('revenue')) return 'commercial_revenue';
    if (normalizedLabel.includes('commercial')) return 'commercial';
    if ((normalizedLabel.includes('tenentimprovement') || normalizedLabel.includes('tenantimprovement')) && normalizedLabel.includes('revenue')) return 'tenent_improvements_revenue';
    if (normalizedLabel.includes('tenentimprovement') || normalizedLabel.includes('tenantimprovement')) return 'tenent_improvements';
    if (normalizedLabel.includes('servicemaintenance') && normalizedLabel.includes('revenue')) return 'service_and_maintenance_revenue';
    if (normalizedLabel.includes('servicemaintenance')) return 'service_and_maintenance';

    return null;
  };

  const isEditableGoalCell = (rowData, field) => {
    if (activeView !== SUMMARY_VIEW.MANUAL) {
      return false;
    }

    if (!field || field === firstColumnName) {
      return false;
    }
    const hasGoalField = Boolean(getGoalFieldFromRow(rowData));
    if (!hasGoalField) {
      return false;
    }

    return Boolean(getYearFromColumn(field) || isTop25Column(field));
  };

  const loadProjectionSummary = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(Api.API_BASE_URL + 'get-financial-summary', {
        headers: getAuthHeaders()
      });

      const result = response?.data?.result;
      const calculatedRows = Array.isArray(result?.calculated)
        ? result.calculated
        : (Array.isArray(result) ? result : []);
      const manualRows = Array.isArray(result?.manual)
        ? result.manual
        : [];

      setSummaryRowsByView({
        [SUMMARY_VIEW.CALCULATED]: calculatedRows,
        [SUMMARY_VIEW.MANUAL]: manualRows
      });
      setColumnName(Array.isArray(response?.data?.ColumnName) ? response.data.ColumnName : []);
    } catch (error) {
      console.error('Error fetching summary:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Load failed',
        detail: 'Unable to load summary data.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSummaryDependencies = async () => {
    setIsRefreshing(true);
    setIsLoading(true);
    try {
      await axios.get(Api.API_BASE_URL + 'get-income-statement', {
        headers: getAuthHeaders()
      });

      await axios.get(Api.API_BASE_URL + 'get-cashflow', {
        headers: getAuthHeaders()
      });

      await loadProjectionSummary();

      toast.current?.show({
        severity: 'success',
        summary: 'Refreshed',
        detail: 'Summary has been refreshed from Income Statement and Cashflow.'
      });
    } catch (error) {
      console.error('Error refreshing summary dependencies:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Refresh failed',
        detail: 'Unable to refresh summary. Please try again.'
      });
      setIsLoading(false);
    } finally {
      setIsRefreshing(false);
    }
  };


  useEffect(() => {
    loadProjectionSummary();
  }, []);

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          field={firstColumnName}
          frozen
          header={firstColumnName}
          style={{ width: '200px', textAlign: 'left', height: '38px' }}
        />

        {columnName.slice(1).map((row, i) => (
          <Column
            key={i + 1}
            header={row}
            style={{ textAlign: 'center', width: '110px', height: '38px' }}
          />
        ))}
      </Row>
    </ColumnGroup>
  );

  const fetchGoalForYear = async (year) => {
    const response = await fetch(Api.API_BASE_URL + 'get-goal', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ year })
    });
    return response.json();
  };

  const saveGoalPayload = async (payload) => {
    const response = await fetch(Api.API_BASE_URL + 'save-goal', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return response.json();
  };

  const isPercentRow = (rowData) => normalizeText(getRowLabel(rowData)).includes('percent') || String(getRowLabel(rowData)).includes('%');

  const renderDecimalCell = (rowData, column) => {
    const value = rowData?.[column.field];
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsedStringValue = Number(value.replace(/[$,%\s,]/g, ''));
      if (!Number.isFinite(parsedStringValue)) {
        return value;
      }
    }

    const numericValue = toNumber(value);
    if (numericValue === 0) {
      return '';
    }

    if (isPercentRow(rowData) || normalizeText(column.field).includes('increasepercent')) {
      return `${numericValue.toFixed(2)} %`;
    }

    if (numericValue < 0) {
      return <span className="summary-negative-value">{formatCurrency(numericValue)}</span>;
    }

    return formatCurrency(numericValue);
  };

  const rowClass = (data) => {
    const normalizedLabel = normalizeText(getRowLabel(data));
    const isSectionRow = [
      'revenueprojection',
      'sales',
      'projectionsandassumption',
      'total',
      'grandtotal',
      'totalprojects ofrevenue',
      'totalprojectsofrevenue'
    ].some((value) => normalizedLabel === value);

    return isSectionRow ? 'summary-section-row' : '';
  };

  const onCellEditComplete = async (e) => {
    const rowData = e.rowData;
    const field = e.field || e.column?.props?.field;

    if (!isEditableGoalCell(rowData, field)) {
      return;
    }

    const parsedNewValue = toNumber(e.newValue);
    const parsedOldValue = toNumber(e.value);
    if (parsedNewValue === parsedOldValue) {
      return;
    }

    const goalScope = getGoalScopeFromColumn(field);
    const goalField = getGoalFieldFromRow(rowData);

    if (!goalScope || !goalField) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Not editable',
        detail: 'This row cannot be saved as a goal value.'
      });
      return;
    }

    const rowIndex = typeof e.rowIndex === 'number' ? e.rowIndex : projectionSummary.findIndex((row) => row === rowData);

    setSummaryRowsByView((prev) => {
      const currentRows = prev[SUMMARY_VIEW.MANUAL] || [];
      if (rowIndex < 0 || rowIndex >= currentRows.length) {
        return prev;
      }
      const nextRows = [...currentRows];
      nextRows[rowIndex] = { ...nextRows[rowIndex], [field]: parsedNewValue };
      return {
        ...prev,
        [SUMMARY_VIEW.MANUAL]: nextRows
      };
    });

    try {
      const goalData = await fetchGoalForYear(goalScope);
      const existingGoal = goalData?.result && typeof goalData.result === 'object' ? goalData.result : {};
      const payload = {
        ...GOAL_DEFAULTS,
        ...existingGoal,
        year: goalScope,
        [goalField]: parsedNewValue
      };

      const saveResult = await saveGoalPayload(payload);
      if (saveResult?.error) {
        throw new Error(saveResult.error);
      }
      toast.current?.show({
        severity: 'success',
        summary: 'Saved',
        detail: `${field} updated successfully.`
      });
    } catch (error) {
      setSummaryRowsByView((prev) => {
        const currentRows = prev[SUMMARY_VIEW.MANUAL] || [];
        if (rowIndex < 0 || rowIndex >= currentRows.length) {
          return prev;
        }
        const nextRows = [...currentRows];
        nextRows[rowIndex] = { ...nextRows[rowIndex], [field]: parsedOldValue };
        return {
          ...prev,
          [SUMMARY_VIEW.MANUAL]: nextRows
        };
      });
      toast.current?.show({
        severity: 'error',
        summary: 'Save failed',
        detail: error?.message || 'Unable to save this cell value.'
      });
    }
  };

  const onCellEditInit = (e) => {
    const field = e.field || e.columnProps?.field || e.column?.props?.field;
    if (!isEditableGoalCell(e.rowData, field)) {
      // PrimeReact doesn't support cancelling edit via preventDefault;
      // dispatch Escape after the edit mode activates to immediately exit it.
      setTimeout(() => {
        const td = e.originalEvent?.target?.closest('td');
        if (td) {
          td.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', keyCode: 27, bubbles: true, cancelable: true }));
        }
      }, 0);
    }
  };

  const cellEditor = (options, isFirstColumn) => {
    const field = options.field || options.column?.props?.field;
    if (isFirstColumn || !isEditableGoalCell(options.rowData, field)) {
      // Return the display value so the cell doesn't go blank when clicked
      const displayVal = renderDecimalCell(options.rowData, { field });
      return <span style={{ pointerEvents: 'none' }}>{displayVal}</span>;
    }

    const editorValue = options.value === null || options.value === undefined ? '' : String(options.value);

    return (
      <InputText
        value={editorValue}
        onChange={(e) => options.editorCallback(e.target.value)}
        className="summary-cell-editor"
        autoFocus
        style={{ width: '100%' }}
      />
    );
  };

  const renderTooltipCell = (rowData, column) => {
    const tooltipId = `tooltip-${(rowData.id || Math.random().toString(36).substr(2, 9))}`;
    const hasTooltip = Boolean(rowData.Tooltip);

    return (
      <div className="summary-label-cell">
        <span>{rowData[column.field]}</span>

        {hasTooltip && (
          <>
            <BsFillInfoCircleFill
              id={tooltipId}
              className='summary-tooltip-icon'
            />
            <Tooltip
              target={`#${tooltipId}`}
              content={rowData['Tooltip']}
              position="top"
              className="custom-tooltip"
            />
          </>
        )}
      </div>
    );
  };

  const tableColumnDefs = useMemo(() => columnName.map((column, index) => ({
    column,
    isFirstColumn: index === 0
  })), [columnName]);

  return (
    <>
      <Toast ref={toast} />
      <div className="summary-page">
        <div className="summary-card">
          <div className="summary-card-header">
            <h2 className="summary-title">Cash Flow Summary</h2>
            <div className="summary-header-row">
              <p className="summary-subtitle">
                {activeView === SUMMARY_VIEW.CALCULATED
                  ? 'Calculated values view (read-only).'
                  : 'Manual values view. Year and goal cells are editable and saved directly.'}
              </p>
              <div className="summary-header-actions">
                <div className="summary-view-switch" role="group" aria-label="Summary data source">
                  <button
                    type="button"
                    className={`summary-view-btn ${activeView === SUMMARY_VIEW.CALCULATED ? 'active' : ''}`}
                    onClick={() => setActiveView(SUMMARY_VIEW.CALCULATED)}
                  >
                    Calculated
                  </button>
                  <button
                    type="button"
                    className={`summary-view-btn ${activeView === SUMMARY_VIEW.MANUAL ? 'active' : ''}`}
                    onClick={() => setActiveView(SUMMARY_VIEW.MANUAL)}
                  >
                    Manual
                  </button>
                </div>
                <button
                  type="button"
                  className="summary-refresh-btn"
                  onClick={refreshSummaryDependencies}
                  disabled={isRefreshing}
                  aria-label="Refresh summary data"
                  title={isRefreshing ? 'Refreshing summary...' : 'Refresh summary'}
                >
                  <PiArrowsClockwiseBold className={isRefreshing ? 'summary-refresh-icon is-spinning' : 'summary-refresh-icon'} />
                </button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="loading-spinner">
              Loading...
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="dataTableSummery p-2">
              <DataTable
                value={projectionSummary}
                headerColumnGroup={headerGroup}
                rowClassName={rowClass}
                tableStyle={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: '0' }}
                scrollable
                scrollHeight="flex"
                size="small"
                className='financial-summary-datatble'
                editMode="cell"
              >
                {tableColumnDefs.map(({ column, isFirstColumn }, index) => (
                  isFirstColumn ? (
                    <Column
                      key={index}
                      field={column}
                      body={renderTooltipCell}
                      style={{ textAlign: 'left', height: '30px', minWidth: '80px' }}
                      frozen
                    />
                  ) : (
                    <Column
                      key={index}
                      field={column}
                      body={renderDecimalCell}
                      bodyClassName={(rowData) => (isEditableGoalCell(rowData, column) ? 'summary-editable-cell' : 'summary-readonly-cell')}
                      editor={(options) => cellEditor(options, false)}
                      onCellEditInit={onCellEditInit}
                      onCellEditComplete={onCellEditComplete}
                      style={{ textAlign: 'center', height: '30px', minWidth: '80px' }}
                    />
                  )
                ))}
              </DataTable>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Summary;
